/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable
 *
 * A semantic `<table>` driven by a `columns` array, matching the shape the
 * product's existing tables already use.
 *
 * Deliberately not included: the table never sorts, filters or pages data. It
 * renders the `data` it is handed and reports intent upward, so a page can drive
 * it from local state, a store or the server without the table guessing.
 *
 * Scope: paginated list tables — roughly up to a couple of hundred rows and a
 * couple of dozen columns, with read-only cells. It renders every row and column
 * it is given, so schema-driven grids with customer-defined columns, inline cell
 * editing and pivots are out of scope and stay on the dedicated grid.
 */
import cx from 'classnames';
import * as React from 'react';

import type { DataTableApi, DataTableColumn, DataTableProps, DataTableSelectionMode, DataTableSort } from '../../types';
import { Checkbox } from '../Checkbox';
import Icon from '../Icon/Icon';
import { Spinner } from '../Spinner';
import DataTablePagination from './DataTablePagination';
import DataTableResizeHandle from './DataTableResizeHandle';
import DataTableRow from './DataTableRow';
import DataTableToolbar from './DataTableToolbar';
import { MIN_COLUMN_WIDTH, useColumnWidths } from './useColumnWidths';
import { useDataTableSelection } from './useDataTableSelection';
import { pinnedCellClassNames, pinnedCellStyle, SELECTION_COLUMN_KEY, usePinnedColumns } from './usePinnedColumns';
import './DataTable.less';

/**
 * Per-instance counter behind the header-label ids. `React.useId` would be the
 * modern equivalent, but this project's `@types/react` is still on 17 where it is
 * not declared. Safe here because the app renders client-side only, so there is
 * no server/client id mismatch to worry about.
 */
let tableInstanceCount = 0;

/** Fallback lower bound for a column with no explicit `width` / `minWidth`. */
const DEFAULT_COLUMN_MIN_WIDTH = 120;
/** Keep in sync with `--ds-dt-selection-col-width` in DataTable.less. */
const SELECTION_COLUMN_WIDTH = 44;

/** Cycles a column's sort: unsorted → ascending → descending → unsorted. */
const nextSort = (column: string, current: DataTableSort | null | undefined): DataTableSort | null => {
  if (!current || current.key !== column) {
    return { key: column, direction: 'asc' };
  }

  return current.direction === 'asc' ? { key: column, direction: 'desc' } : null;
};

/**
 * Whether sorting this column does anything. A column can declare `sortable`
 * while the table has no `onSortChange` — the affordance would then be inert, so
 * neither the button nor `aria-sort` should claim otherwise.
 */
const isSortable = <TData,>(column: DataTableColumn<TData>, onSortChange?: unknown) =>
  Boolean(column.sortable && onSortChange);

const ariaSortFor = <TData,>(
  column: DataTableColumn<TData>,
  sort: DataTableSort | null | undefined,
  onSortChange?: unknown
) => {
  if (!isSortable(column, onSortChange)) return undefined;
  if (sort?.key !== column.key) return 'none' as const;

  return sort.direction === 'asc' ? ('ascending' as const) : ('descending' as const);
};

/** A column's accessible name, for places that need a string rather than a node. */
const columnLabel = <TData,>(column: DataTableColumn<TData>) =>
  column.headerLabel ?? (typeof column.header === 'string' ? column.header : column.key);

/** Normalises the `selectable` prop's boolean shorthand. */
const selectionModeFor = (selectable: boolean | DataTableSelectionMode): DataTableSelectionMode | false => {
  if (selectable === true) return 'multiple';
  if (selectable === false) return false;

  return selectable;
};

function DataTable<TData>({
  data,
  columns,
  rowKey = 'id' as keyof TData,
  toolbar,
  pagination,
  selectable = false,
  selectedRowKeys,
  defaultSelectedRowKeys,
  onSelectionChange,
  isRowSelectable,
  resizableColumns = true,
  columnWidthStorageKey,
  onColumnResize,
  sort,
  onSortChange,
  onRowClick,
  rowClassName,
  loading = false,
  loadingText = 'Loading',
  emptyText = 'No records found',
  error,
  emptyValue = '-',
  maxHeight,
  stickyHeader = true,
  size = 'medium',
  getRowLabel,
  labels,
  apiRef,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: DataTableProps<TData>) {
  const selectionMode = selectionModeFor(selectable);

  /**
   * Warn once per table when rows have no usable id. Selection and React keys both
   * depend on one, and falling back to the row's position silently breaks
   * selection as soon as the data is re-sorted or paged.
   */
  const warnedMissingRowKey = React.useRef(false);

  const getRowKey = React.useCallback(
    (row: TData, index: number): string => {
      if (typeof rowKey === 'function') {
        return rowKey(row);
      }

      const value = (row as Record<string, unknown>)[rowKey as string];

      if (value === null || value === undefined) {
        if (process.env.NODE_ENV !== 'production' && !warnedMissingRowKey.current) {
          warnedMissingRowKey.current = true;
          // eslint-disable-next-line no-console
          console.warn(
            `[DataTable] No "${String(rowKey)}" on the rows given to the table ` +
              `(table: "${ariaLabel ?? 'unnamed'}"), so each row's position is being used as its id. ` +
              'Selection will attach to the wrong rows once the data is sorted or paged. ' +
              'Pass `rowKey` naming the field that identifies a row, e.g. rowKey="syncariId", ' +
              'or a function for a composite id.'
          );
        }

        return String(index);
      }

      return String(value);
    },
    [ariaLabel, rowKey]
  );

  const {
    selectedKeySet,
    selectAllState,
    toggleRow,
    toggleAll,
    clearSelection,
    hasSelectableRows,
  } = useDataTableSelection({
    data,
    getRowKey,
    mode: selectionMode,
    selectedRowKeys,
    defaultSelectedRowKeys,
    onSelectionChange,
    isRowSelectable,
  });

  const {
    widths,
    previewWidth,
    commitWidth,
    resetWidths,
    registerCol,
    registerTable,
    beginResize,
    endResize,
    resizingKey,
  } = useColumnWidths({
    storageKey: columnWidthStorageKey,
    onColumnResize,
    resizeEnabled: columns.some((column) => column.resizable ?? resizableColumns),
    debugLabel: ariaLabel,
  });

  /** A user-dragged width wins over the column's declared one. */
  const widthFor = (column: DataTableColumn<TData>) => widths[column.key] ?? column.width;

  const pinnedOffsets = usePinnedColumns({
    columns,
    widths,
    hasSelection: Boolean(selectionMode),
    selectionWidth: SELECTION_COLUMN_WIDTH,
  });

  /**
   * Imperative escape hatches for state the table owns and a caller cannot
   * otherwise reach: persisted column widths, and a selection that may include
   * keys from pages the table never saw. Both are things a toolbar button needs
   * ("Reset columns", "Clear selection") and neither fits a normal prop.
   */
  React.useEffect(() => {
    if (!apiRef) return undefined;

    apiRef.current = { resetColumnWidths: resetWidths, clearSelection };

    return () => {
      apiRef.current = null;
    };
  }, [apiRef, clearSelection, resetWidths]);

  /**
   * Width below which the table scrolls horizontally rather than squeezing
   * columns past readability. Above it, `table-layout: fixed` lets columns
   * without an explicit width absorb the extra space.
   */
  const minTableWidth = React.useMemo(() => {
    const columnsWidth = columns.reduce(
      (total, column) => total + (widths[column.key] ?? column.width ?? column.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH),
      0
    );

    return columnsWidth + (selectionMode ? SELECTION_COLUMN_WIDTH : 0);
  }, [columns, selectionMode, widths]);

  const handleSort = (column: DataTableColumn<TData>) => {
    if (!isSortable(column, onSortChange)) return;
    onSortChange?.(nextSort(column.key, sort));
  };

  const headerRefs = React.useRef<Record<string, HTMLTableCellElement | null>>({});

  /**
   * Callers routinely pass inline arrows for `onRowClick`, which would change
   * identity on every render and defeat the row-level `React.memo` — making each
   * row re-render whenever anything in the table changes. The latest value is read
   * through a ref so the handler below stays stable for the component's lifetime.
   */
  const onRowClickRef = React.useRef(onRowClick);
  onRowClickRef.current = onRowClick;

  const handleRowClick = React.useCallback((row: TData, rowIndex: number) => {
    onRowClickRef.current?.(row, rowIndex);
  }, []);

  // `undefined` must be forwarded as-is: it is what tells a row it is not clickable.
  const stableRowClick = onRowClick ? handleRowClick : undefined;

  /**
   * Header cells point at their own label rather than deriving a name from their
   * content, so the resize handle's label does not get appended to the column
   * name that assistive tech announces.
   */
  const instanceIdRef = React.useRef<string>();
  if (!instanceIdRef.current) {
    tableInstanceCount += 1;
    instanceIdRef.current = `ds-data-table-${tableInstanceCount}`;
  }
  const labelIdFor = (columnKey: string) => `${instanceIdRef.current}-${columnKey}`;

  /**
   * Starting width for a drag. Columns that flex have no declared width, so the
   * rendered cell is measured to pick up where the layout actually put it.
   */
  const currentWidthFor = (column: DataTableColumn<TData>) => () => {
    // Measure the rendered cell first: it reflects live drag updates and the
    // width the layout actually gave a flexible column.
    const measured = headerRefs.current[column.key]?.getBoundingClientRect().width;
    if (measured) return measured;

    return widthFor(column) ?? MIN_COLUMN_WIDTH;
  };

  const resizeLabel = labels?.resizeColumn ?? ((label: string) => `Resize ${label} column`);
  const selectRowLabel =
    labels?.selectRow ??
    (({ rowLabel, rowNumber }: { rowLabel?: string; rowNumber: number }) =>
      rowLabel ? `Select ${rowLabel}` : `Select row ${rowNumber}`);

  const columnCount = columns.length + (selectionMode ? 1 : 0);
  const hasRows = data.length > 0 && !error;
  const placeholder = error ?? emptyText;
  const hasPinnedColumns = Object.keys(pinnedOffsets).length > 0;

  const table = (
    <div
      className={cx('ds-data-table__scroll', { 'ds-data-table__scroll--sticky-header': stickyHeader })}
      style={{ maxHeight }}>
      <table
        ref={registerTable}
        className="ds-data-table__table"
        style={{ minWidth: minTableWidth }}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}>
        {/* Widths live here so `table-layout: fixed` can honour them without
            per-cell inline styles. */}
        <colgroup>
          {selectionMode && <col style={{ width: SELECTION_COLUMN_WIDTH }} />}
          {columns.map((column) => {
            const width = widthFor(column);
            return (
              <col
                key={column.key}
                ref={(node) => registerCol(column.key, node)}
                style={width ? { width } : undefined}
              />
            );
          })}
        </colgroup>

        <thead className="ds-data-table__head">
          <tr>
            {selectionMode && (
              <th
                scope="col"
                className={cx(
                  'ds-data-table__header-cell',
                  'ds-data-table__header-cell--selection',
                  pinnedCellClassNames(SELECTION_COLUMN_KEY, pinnedOffsets, 'header-cell')
                )}
                style={pinnedCellStyle(SELECTION_COLUMN_KEY, pinnedOffsets)}>
                {/* Single-select has nothing to select all of. */}
                {selectionMode === 'multiple' && (
                  <Checkbox
                    checked={selectAllState === 'all'}
                    indeterminate={selectAllState === 'some'}
                    disabled={!hasSelectableRows}
                    onChange={toggleAll}
                    aria-label={labels?.selectAll ?? 'Select all rows'}
                  />
                )}
              </th>
            )}

            {columns.map((column) => {
              const isSorted = sort?.key === column.key;
              const isInteractive = isSortable(column, onSortChange);
              const isResizable = column.resizable ?? resizableColumns;

              return (
                <th
                  key={column.key}
                  ref={(node) => {
                    headerRefs.current[column.key] = node;
                  }}
                  scope="col"
                  aria-labelledby={labelIdFor(column.key)}
                  aria-sort={ariaSortFor(column, sort, onSortChange)}
                  className={cx(
                    'ds-data-table__header-cell',
                    `ds-data-table__header-cell--${column.align ?? 'left'}`,
                    { 'ds-data-table__header-cell--sortable': isInteractive },
                    pinnedCellClassNames(column.key, pinnedOffsets, 'header-cell'),
                    column.headerClassName
                  )}
                  style={pinnedCellStyle(column.key, pinnedOffsets)}>
                  {isInteractive ? (
                    <button type="button" className="ds-data-table__header-button" onClick={() => handleSort(column)}>
                      <span id={labelIdFor(column.key)} className="ds-data-table__header-label">
                        {column.header}
                      </span>
                      <Icon
                        name="actionable/arrow-up"
                        size="small"
                        className={cx('ds-data-table__sort-icon', {
                          'ds-data-table__sort-icon--active': isSorted,
                          'ds-data-table__sort-icon--desc': isSorted && sort?.direction === 'desc',
                        })}
                      />
                    </button>
                  ) : (
                    <span id={labelIdFor(column.key)} className="ds-data-table__header-label">
                      {column.header}
                    </span>
                  )}

                  {isResizable && (
                    <DataTableResizeHandle
                      columnKey={column.key}
                      label={columnLabel(column)}
                      minWidth={Math.max(MIN_COLUMN_WIDTH, column.minWidth ?? 0)}
                      width={widthFor(column)}
                      getCurrentWidth={currentWidthFor(column)}
                      onPreview={previewWidth}
                      onCommit={commitWidth}
                      onResizeStart={beginResize}
                      onResizeEnd={endResize}
                      isResizing={resizingKey === column.key}
                      formatLabel={resizeLabel}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        {/* The one place that decides which rows exist — the seam a future
            virtualiser would replace. */}
        <tbody className="ds-data-table__body">
          {hasRows ? (
            data.map((row, rowIndex) => {
              const key = getRowKey(row, rowIndex);
              const rowLabel = getRowLabel?.(row, rowIndex);

              return (
                <DataTableRow
                  key={key}
                  row={row}
                  rowIndex={rowIndex}
                  rowKey={key}
                  columns={columns}
                  emptyValue={emptyValue}
                  selectionMode={selectionMode}
                  selected={selectedKeySet.has(key)}
                  selectionDisabled={Boolean(isRowSelectable && !isRowSelectable(row))}
                  selectionLabel={selectRowLabel({ rowLabel, rowNumber: rowIndex + 1 })}
                  selectionName={`${instanceIdRef.current}-selection`}
                  onToggleSelect={toggleRow}
                  onRowClick={stableRowClick}
                  pinnedOffsets={pinnedOffsets}
                  className={rowClassName?.(row, rowIndex)}
                />
              );
            })
          ) : (
            <tr className="ds-data-table__placeholder-row">
              <td colSpan={columnCount} className="ds-data-table__placeholder">
                {loading ? null : placeholder}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div
      className={cx(
        'ds-data-table',
        `ds-data-table--${size}`,
        {
          'ds-data-table--resizing': resizingKey !== null,
          'ds-data-table--has-pinned': hasPinnedColumns,
          // Which element sits at each edge decides which corners round. CSS
          // cannot infer it: the loading spinner wraps the scroll container and
          // breaks the sibling order a positional selector would need.
          'ds-data-table--has-toolbar': Boolean(toolbar),
          'ds-data-table--has-footer': Boolean(pagination),
        },
        className
      )}>
      {toolbar && <DataTableToolbar {...toolbar} />}

      {loading ? (
        <Spinner label={loadingText} className="ds-data-table__loading">
          {table}
        </Spinner>
      ) : (
        table
      )}

      {pagination && <DataTablePagination {...pagination} />}
    </div>
  );
}

DataTable.displayName = 'DataTable';

export default DataTable;
